import { z } from "zod";

export const GRID_IDS = [
	"ShipmentBoard",
	"AddressBook",
	"AddressBookModal",
	"AddressBookAddress",
	"Admin",
	"AutoDispatch",
	"ARDashboardCarriers",
	"ARDashboardSalesPerformance",
	"Commodities",
	"Loads",
	"Notifications",
	"ShipmentNotificationModal",
	"Payment",
	"SelectedInvoicesPayment",
	"QuoteBoard",
	"Reports",
	"ShipmentDetailsReport",
	"RoutingGuide",
	"RoutingGuideModalCarriers",
	"RoutingGuideModalLanes",
	"ShipmentExceptionModal",
	"Templates",
	"TemplatesModal",
	"VendorManagement",
] as const;

export const gridIdSchema = z.enum(GRID_IDS);
export type GridId = z.infer<typeof gridIdSchema>;

/**
 * Resolves a numeric `GridIdentifiers` value (e.g. `1`) to its API enum name
 * (e.g. `"ShipmentBoard"`). The numeric ids are 1-based and ordered to match
 * `GRID_IDS`, so id `N` maps to `GRID_IDS[N - 1]`.
 */
export const getGridIdName = (numericId: number): GridId | undefined =>
	GRID_IDS[numericId - 1];

export const COLUMN_VISIBILITY_VALUES = [
	"Internal",
	"External",
	"All",
] as const;
// `.catch` here is Zod's fallback API, not a Promise method.
// `z.preprocess` normalizes any casing the API may return (e.g. `"internal"`,
// `"INTERNAL"`) or numeric enum codes (`1`/`2`/`3`, matching
// `gridColumnVisibilityEnums`) to the canonical PascalCase enum value so the
// filter in `applyGridPrefs` keeps matching after a round-trip through the
// server.
const NUMERIC_VISIBILITY: Record<
	number,
	(typeof COLUMN_VISIBILITY_VALUES)[number]
> = { 1: "Internal", 2: "External", 3: "All" };

export const columnVisibilitySchema = z
	.preprocess((value) => {
		if (typeof value === "number") {
			return NUMERIC_VISIBILITY[value] ?? value;
		}
		if (typeof value === "string") {
			if (/^\d+$/.test(value)) {
				return NUMERIC_VISIBILITY[Number(value)] ?? value;
			}
			const match = COLUMN_VISIBILITY_VALUES.find(
				(v) => v.toLowerCase() === value.toLowerCase(),
			);
			return match ?? value;
		}
		return value;
	}, z.enum(COLUMN_VISIBILITY_VALUES))
	// eslint-disable-next-line unicorn/prefer-top-level-await -- Zod fallback API
	.catch("All");
export type ColumnVisibility = z.infer<typeof columnVisibilitySchema>;

export const gridColumnPreferenceSchema = z.object({
	columnId: z.string(),
	isVisible: z.boolean(),
	widthSize: z.number(),
	ordinalPosition: z.number(),
	visibility: columnVisibilitySchema,
});
export type GridColumnPreference = z.infer<typeof gridColumnPreferenceSchema>;

/**
 * The API may return `gridId` as either the enum name (e.g. `"ShipmentBoard"`)
 * or the numeric string id (e.g. `"1"`). Preprocess normalizes the latter to
 * the enum name so downstream consumers always see a `GridId`.
 */
const gridIdValueSchema = z.preprocess((value) => {
	if (typeof value !== "string") return value;
	if (!/^\d+$/.test(value)) return value;
	return getGridIdName(Number(value)) ?? value;
}, gridIdSchema);

export const gridPreferenceSchema = z.object({
	gridId: gridIdValueSchema,
	columns: z.array(gridColumnPreferenceSchema),
	itemsPerPage: z.number().int().positive().default(25),
});
export type GridPreference = z.infer<typeof gridPreferenceSchema>;

/**
 * User preferences are stored as a map keyed by a stable numeric id (as a string)
 * to a grid preference entry. Numeric-string keys are validated via regex.
 */
export const userPreferencesSchema = z.record(
	z.string().regex(/^\d+$/, "preference key must be a numeric string"),
	gridPreferenceSchema,
);
export type UserPreferences = z.infer<typeof userPreferencesSchema>;
